import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import CoachesTable, { Coach } from '../components/Coaches/CoachesTable';
import AddCoachForm, { CoachFormData } from '../components/Coaches/AddCoachForm';
import { useCoachContext } from '../context/CoachContext';
import { fetchCoaches, Coach as APICoach } from '../store/AdminSlices/coachSlice';
import { AppDispatch, RootState } from '../store/store';

const CoachesPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { coaches, loading } = useSelector((state: RootState) => state.coach);
  const { setIsAddingCoach, setBackHandler } = useCoachContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const showAddForm = searchParams.get('add') === 'true';
  const [filteredCoaches, setFilteredCoaches] = useState<Coach[]>([]);

  const handleAddCoach = () => {
    setSearchParams({ add: 'true' });
  };

  const handleCloseForm = useCallback(() => {
    setSearchParams({});
  }, [setSearchParams]);

  // Fetch coaches on mount
  useEffect(() => {
    dispatch(fetchCoaches());
  }, [dispatch]);

  // Convert API coaches to table format and update filtered coaches
  useEffect(() => {
    if (coaches && Array.isArray(coaches) && coaches.length > 0) {
      const formattedCoaches: Coach[] = coaches.map((coach: APICoach) => ({
        id: coach.id,
        name: coach.name,
        email: coach.email,
        image: coach.avatar,
        status: (coach.approved ? 'Active' : 'Pending Activation') as 'Active' | 'Pending Activation',
        areaOfExpertise: coach.area_of_expertise || '-',
        completedSessions: coach.completed_sessions || 0,
        lastSession: coach.last_session 
          ? new Date(coach.last_session).toLocaleDateString('en-US', {
              month: 'long',
              day: 'numeric',
              year: 'numeric',
            })
          : '-',
      }));
      setFilteredCoaches(formattedCoaches);
    }
  }, [coaches]);

  const handleSubmitForm = (data: CoachFormData) => {
    const parsedCompleted = Number.parseInt(data.completedSessions, 10) || 0;
    const formattedLastSession = data.lastSession
      ? new Date(data.lastSession).toLocaleDateString('en-US', {
          month: 'long',
          day: 'numeric',
          year: 'numeric',
        })
      : '-';

    const newCoach: Coach = {
      id: String(filteredCoaches.length + 1),
      name: `${data.firstName} ${data.lastName}`,
      email: data.email,
      status: 'Pending Activation',
      areaOfExpertise: data.areaOfExpertise,
      completedSessions: parsedCompleted,
      lastSession: formattedLastSession,
    };

    const updatedCoaches = [...filteredCoaches, newCoach];
    setFilteredCoaches(updatedCoaches);
    setSearchParams({});
  };

  useEffect(() => {
    setIsAddingCoach(showAddForm);
    if (showAddForm) {
      setBackHandler(handleCloseForm);
    }
  }, [showAddForm, setIsAddingCoach, setBackHandler, handleCloseForm]);

  useEffect(() => {
    return () => {
      setIsAddingCoach(false);
    };
  }, [setIsAddingCoach]);

  const handleSearch = (query: string) => {
    if (!query.trim()) {
      if (coaches && Array.isArray(coaches) && coaches.length > 0) {
        const formattedCoaches: Coach[] = coaches.map((coach: APICoach) => ({
          id: coach.id,
          name: coach.name,
          email: coach.email,
          image: coach.avatar,
          status: (coach.approved ? 'Active' : 'Pending Activation') as 'Active' | 'Pending Activation',
          areaOfExpertise: coach.area_of_expertise || '-',
          completedSessions: coach.completed_sessions || 0,
          lastSession: coach.last_session 
            ? new Date(coach.last_session).toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
              })
            : '-',
        }));
        setFilteredCoaches(formattedCoaches);
      }
      return;
    }

    const lowercaseQuery = query.toLowerCase();
    const filtered = filteredCoaches.filter(
      (coach) =>
        coach.name.toLowerCase().includes(lowercaseQuery) ||
        coach.email.toLowerCase().includes(lowercaseQuery) ||
        coach.areaOfExpertise.toLowerCase().includes(lowercaseQuery)
    );

    setFilteredCoaches(filtered);
  };

  // Handle coach ID from URL query parameter to auto-navigate to coach profile
  useEffect(() => {
    const coachId = searchParams.get('id');
    if (coachId && filteredCoaches.length > 0) {
      const coach = filteredCoaches.find(c => c.id === coachId);
      if (coach) {
        navigate(`/coaches/${coach.id}`, { replace: true });
      }
    }
  }, [searchParams, filteredCoaches, navigate]);

  const handleViewProfile = (coach: Coach) => {
    navigate(`/coaches/${coach.id}`);
  };

  const handleUpdateTarget = (coach: Coach) => {
    navigate(`/coaches/${coach.id}?action=updateTarget`);
  };

  const handleMessage = (coach: Coach) => {
    // Navigate to messages page with coach user_id
    // The MessagesPage will handle finding the conversation with this coach
    navigate(`/messages?userId=${coach.id}&userType=coach`);
  };

  if (showAddForm) {
    return (
      <AddCoachForm
        onClose={handleCloseForm}
        onSubmit={handleSubmitForm}
      />
    );
  }

  return (
    <CoachesTable
      coaches={filteredCoaches}
      onAddCoach={handleAddCoach}
      onViewProfile={handleViewProfile}
      onUpdateTarget={handleUpdateTarget}
      onMessage={handleMessage}
      onSearch={handleSearch}
      loading={loading}
    />
  );
};

export default CoachesPage;
